import { Box, Grid } from '@mui/material';
import { Button, DatePicker, Select, Table, Tag } from 'antd';
import React, { useEffect, useState } from 'react'
import { DATE_FORMAT, LevelReading } from '../../Components/common/constants';
import dayjs from 'dayjs';
import AuthService from '../../Components/auth/AuthService';
import { hideLoading, showLoading, useLoadingDispatch } from '../../context/loadingContext';
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { PrinterFilled } from '@ant-design/icons';
import Message from '../../Components/common/Message';
import { showMessage } from '../../Components/Utils/message';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Auth = new AuthService();

const DefectReport = () => {
    var layoutDispatch = useLoadingDispatch();

    const [dateRange, setDateRange] = useState([
        dayjs().startOf("month"),
        dayjs().endOf("month"),
    ]);
    const [reportData, setReportData] = useState([]);
    const [allVehicles, setAllVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);

    const getReportData = async (dates = dateRange, vehicleId) => {
        showLoading(layoutDispatch);

        try {
            const response = await Auth.post("/report/defect-sheet", {
                start_date: dates[0].format("YYYY-MM-DD"),
                end_date: dates[1].format("YYYY-MM-DD"),
                vehicle_id: vehicleId
            });

            if (response.ack) {
                setReportData(response.vehicle_defect_sheets || []);
            } else {
                setReportData([]);
            }
        } catch (error) {
            console.error("Error fetching defect report data:", error);
            Message.error("Failed to fetch defect report data");
        } finally {
            hideLoading(layoutDispatch);
        }
    };

    const addSheetToPDF = (pdf, sheet) => {
        let startrow = 25;
        const typeText = sheet.type === 1 ? "Checkout" : "Return";
        const formatted_date = sheet.delivery_date ? moment(sheet.delivery_date).format(DATE_FORMAT) : "";

        const pageContent = function (data) {

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // TITLE
            pdf.setFontSize(22);
            pdf.setTextColor(0, 0, 0);
            pdf.setFont(undefined, "bold");
            pdf.text(`Vehicle Defect Report - ${typeText}`, pageWidth / 2, 40, { align: "center" });

            // HEADER BOX (increased height)
            pdf.setFillColor(245, 247, 250);
            pdf.roundedRect(35, 50, pageWidth - 70, 65, 4, 4, "F");

            pdf.setFontSize(14);
            pdf.setTextColor(0, 0, 0);

            const labelX = 55;
            const valueX = 120;

            const rightLabelX = pageWidth / 2 + 25;
            const rightValueX = rightLabelX + 50;

            const y1 = 72;
            const gap = 20;

            // LEFT SIDE
            pdf.setFont(undefined, "bold");
            pdf.text("Route", labelX, y1);
            pdf.text("Vehicle", labelX, y1 + gap);
            pdf.text("Driver", labelX, y1 + gap * 2);

            pdf.setFont(undefined, "normal");
            pdf.text(sheet.route || "N/A", valueX, y1);
            pdf.text(sheet.registration_number || "N/A", valueX, y1 + gap);
            pdf.text(sheet.driver || "N/A", valueX, y1 + gap * 2);

            // RIGHT SIDE
            pdf.setFont(undefined, "bold");
            pdf.text("Date", rightLabelX, y1);

            pdf.setFont(undefined, "normal");
            pdf.text(formatted_date || "N/A", rightValueX, y1);

            // DIVIDER
            pdf.setDrawColor(200, 200, 200);
            pdf.line(35, 125, pageWidth - 35, 125);

            // FOOTER
            pdf.setFontSize(10);
            pdf.setTextColor(100);
            pdf.text(
                `Page ${pdf.internal.getCurrentPageInfo().pageNumber}`,
                pageWidth / 2,
                pageHeight - 10,
                { align: "center" }
            );
        };

        const optionsList = [
            { label: "Battery Security", key: "battery_security" },
            { label: "Spray Suppression", key: "spray_suppression" },
            { label: "Registration Plates", key: "registration_plates" },
            { label: "Seat Belt", key: "seat_belt" },
            { label: "Warning Lights", key: "warning_lights" },
            { label: "Wipers", key: "wipers" },
            { label: "Exhaust Smoke", key: "exhaust_smoke" },
            { label: "Reflectors, Markers and Lights", key: "reflectors" },
            { label: "Indicators", key: "indicators" },
            { label: "Adblue", key: "adblue" },
            { label: "Cab Steps", key: "cab_steps" },
            { label: "Fuel Leaks", key: "fuel_leaks" },
            { label: "Wheels Tyres Fixing", key: "wheels_tyres_fixing" },
            { label: "Steering", key: "steering" },
            { label: "Brakes", key: "brakes" },
            { label: "Air Build", key: "air_build" },
            { label: "Odometer", key: "odometer" },
            { label: "Tachograph", key: "tachograph" },
            { label: "Security Load", key: "security_load" },
            { label: "Mirrors", key: "mirrors" },
            { label: "Fit to Drive", key: "fit_to_drive" },
            { label: "Licence Visible", key: "licence_visible" },
            { label: "Glass Visibility", key: "glass_visibility" },
            { label: "Doors", key: "doors" },
            { label: "First Aid Kit", key: "first_aid_kit" },
            { label: "Passenger Seat Belt", key: "passenger_seat_belt" },
            { label: "Fire Extinguisher", key: "fire_extinguisher" },
            { label: "Tail Lift", key: "tail_lift" },
            { label: "Alternative Fuels", key: "alternative_fuels" },
            { label: "Horn", key: "horn" },
            { label: "Brake Lines", key: "brake_lines" },
            { label: "Body Condition", key: "body_condition" },
            { label: "Coupling Security", key: "coupling_security" },
            { label: "Electrical Connections", key: "electrical_connections" },
            { label: "Saloon Lighting", key: "saloon_lighting" },
            { label: "Heating", key: "heating" },
            { label: "Wheelchair", key: "wheelchair" },
            { label: "Communication Devices", key: "communication_devices" },
            { label: "Prominent Warning Signage", key: "prominent_warning_signage" },
            { label: "Side Under Run Protection", key: "side_under_run_protection" },
            { label: "Camera System", key: "camera_system" },
            { label: "Fresnel Lenses", key: "fresnel_lenses" },
            { label: "High Voltage Emergency Cut Off Switch", key: "high_voltage_emergency" },
            { label: "Adas", key: "adas" },
            { label: "Pumptruck Working", key: "pumptruck_working" }
        ];

        const defectRows = [];
        for (let i = 0; i < optionsList.length; i += 3) {
            const row = [];
            for (let j = 0; j < 3; j++) {
                if (i + j < optionsList.length) {
                    const opt = optionsList[i + j];
                    const value = sheet[opt.key];
                    const statusText = value ? "Yes" : "No";

                    row.push(
                        { content: opt.label, styles: { fontStyle: "bold" } },
                        { content: statusText, styles: { fillColor: value ? [198, 239, 206] : [255, 199, 206], textColor: value ? [0, 97, 0] : [156, 0, 6], halign: "center" } }
                    );
                } else {
                    row.push({ content: "", styles: {} }, { content: "", styles: {} });
                }
            }
            defectRows.push(row);
        }

        pdf.autoTable({
            didDrawPage: pageContent,
            theme: "grid",
            margin: { top: startrow + 100 },
            body: defectRows,
            styles: { fontSize: 10, cellPadding: 5, valign: "middle" },
            headStyles: { fillColor: [24, 144, 255], textColor: 255, halign: "center" }
        });

        let finalY = pdf.lastAutoTable.finalY + 20;

        // Readings
        const getLevelReading = (id) => LevelReading.find(r => r.id === id)?.title || "N/A";
        let odo = sheet.type === 1 ? sheet.odo_meter : sheet.return_odo_meter;
        let fuel = getLevelReading(sheet.type === 1 ? sheet.fuel : sheet.return_fuel);
        let adblue = getLevelReading(sheet.type === 1 ? sheet.ad_blue : sheet.return_ad_blue);
        let ref_fuel = getLevelReading(sheet.type === 1 ? sheet.refrigerator_fuel : sheet.return_refrigerator_fuel);

        const infoRows = [
            [
                "Odometer:\n" + (odo || "N/A"),
                "Fuel Level:\n" + fuel,
                "AdBlue Level:\n" + adblue
            ],
            [
                "Refrigerator Fuel:\n" + ref_fuel,
                "Fuel Card " + (sheet.type === 1 ? "Collected" : "Returned") + ":\n" + (sheet.fuel_card === 1 ? "Yes" : "No"),
                "Tacho " + (sheet.type === 1 ? "Removed" : "Returned") + ":\n" + (sheet.tacho === 1 ? "Yes" : "No")
            ]
        ];

        pdf.autoTable({
            startY: finalY,
            body: infoRows,
            theme: "grid",
            styles: { fontSize: 10, halign: "center", valign: "middle", cellPadding: 8 }
        });

        finalY = pdf.lastAutoTable.finalY + 20;

        // Notes
        let notesVal = sheet.type === 1 ? sheet.notes : sheet.notes_return;
        if (notesVal) {
            // extra spacing before notes
            finalY += 10;

            if (finalY + 40 > pdf.internal.pageSize.getHeight()) {
                pdf.addPage();
                finalY = 40;
            }

            // Section title
            pdf.setFontSize(13);
            pdf.setFont(undefined, "bold");
            pdf.setTextColor(40, 40, 40);
            pdf.text("Notes", 40, finalY);

            finalY += 15;

            // Notes text
            pdf.setFontSize(11);
            pdf.setFont(undefined, "normal");
            pdf.setTextColor(60, 60, 60);

            const textWidth = pdf.internal.pageSize.getWidth() - 80;
            let splitNotes = pdf.splitTextToSize(notesVal, textWidth);

            pdf.text(splitNotes, 40, finalY);

            finalY += splitNotes.length * 14;
        }

        // Signature
        let signature = sheet.type === 1 ? sheet.signature : sheet.return_signature;
        if (signature) {
            finalY += 10;
            if (finalY + 110 > pdf.internal.pageSize.getHeight()) {
                pdf.addPage();
                finalY = 40;
            }
            pdf.text("Signature: ", 40, finalY);
            finalY += 10;
            try {
                pdf.addImage(signature, 'PNG', 40, finalY, 150, 100);
            } catch (e) {
                console.log("Could not add signature image to PDF", e);
            }
        }
    };

    const handleDownloadAllPDF = async () => {
        if (!reportData || reportData.length === 0) {
            Message.warning("No defect sheets to download.");
            return;
        }

        let pdf = new jsPDF("p", "pt", "A4");

        reportData.forEach((sheet, index) => {
            if (index > 0) {
                pdf.addPage();
            }
            addSheetToPDF(pdf, sheet);
        });

        pdf.save(`All_Defect_Reports_${moment().format("DD-MM-YYYY")}.pdf`);
    };

    const handleDownloadPDF = (sheet) => {
        let pdf = new jsPDF("p", "pt", "A4");

        addSheetToPDF(pdf, sheet);

        const formatted_date = sheet.delivery_date ? moment(sheet.delivery_date).format("DD-MMM-YYYY") : "";
        pdf.save(`Defect_Report_${sheet.registration_number || 'Vehicle'}_${formatted_date || moment().format("DD-MM-YYYY")}.pdf`);
    };

    const columns = [
        {
            title: 'Delivery Date',
            dataIndex: 'delivery_date',
            key: 'delivery_date',
            render: (text) => text ? moment(text).format('DD-MM-YYYY') : 'N/A'
        },
        {
            title: 'Route',
            dataIndex: 'route',
            key: 'route',
        },
        {
            title: 'Vehicle',
            dataIndex: 'registration_number',
            key: 'registration_number',
        },
        {
            title: 'Driver',
            dataIndex: 'driver',
            key: 'driver',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => <Tag color={type === 1 ? 'blue' : 'green'}>{type === 1 ? "Checkout" : "Return"}</Tag>
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<PrinterFilled />}
                    size="small"
                    onClick={() => handleDownloadPDF(record)}
                >
                    Download PDF
                </Button>
            ),
        },
    ];

    const getAllVehicles = async () => {
        showLoading(layoutDispatch);
        try {
            const response = await Auth.fetch(`/vehicle/vehicles`);
            if (response) {
                hideLoading(layoutDispatch);
                if (response.ack) {
                    setAllVehicles(response.vehicles);
                }
            }
        } catch (err) {
            console.log(err);
        } finally {
            hideLoading(layoutDispatch);
        }
    }

    useEffect(() => {
        getAllVehicles();
        getReportData(dateRange);
    }, []);

    return (
        <div>
            <h2 style={{ marginBottom: '10px', marginTop: 0 }}>Vehicle Defect Report</h2>

            <Grid
                container
                spacing={2}
                sx={{ alignItems: "center", marginBottom: "20px" }}
            >
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: "10px",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <RangePicker
                                format={DATE_FORMAT}
                                size="large"
                                style={{ width: 320 }}
                                value={dateRange}
                                onChange={(dates) => {
                                    if (!dates) return;
                                    setDateRange(dates);
                                    getReportData(dates);
                                }}
                                allowClear={false}
                            />

                            <Select
                                showSearch
                                allowClear
                                size="large"
                                style={{ width: 200 }}
                                placeholder="Registration No"
                                value={selectedVehicle?.registration_number}
                                onChange={(value, option) => {
                                    const selected = allVehicles.find(v => v.registration_number === value) || null;
                                    setSelectedVehicle(selected?.id);
                                    getReportData(dateRange, selected?.id);
                                }}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {allVehicles.map(vehicle => (
                                    <Option key={vehicle._id} value={vehicle.registration_number} label={vehicle.registration_number}>
                                        {vehicle.registration_number}
                                    </Option>
                                ))}
                            </Select>

                            <Button
                                type="primary"
                                size="large"
                                onClick={() => getReportData(dateRange)}
                            >
                                Load Report
                            </Button>
                        </Box>

                        <Button
                            type="primary"
                            size="large"
                            onClick={handleDownloadAllPDF}
                        >
                            Download All
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            <Table
                columns={columns}
                dataSource={reportData}
                rowKey={(record) => record.id || Math.random().toString()}
                bordered
                pagination={false}
            />
        </div>
    )
}

export default DefectReport
